import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import migrationService from '../services/migrationService';
import apiClient from '../services/api';
import { formatAmount } from '../utils/formatHelpers';
import { detectDefaultCurrency } from '../utils/currencyUtils';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import AccountIconBadge from '../components/AccountIconBadge';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';

import onboardingService from '../services/onboardingService';
import { useOnboardingRefresh } from '../contexts/OnboardingRefreshContext';

const CuentasPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  // 17 sep 2026 (pedido de Augusto: "se puede tener una version completa
  // en ingles?"): esta pantalla no tenia NINGUN mecanismo de idioma --
  // todo el texto visible estaba fijo en español. Se agrega el mismo
  // patron ya usado en Reports.js/StatementImporter.js: isSpanish
  // calculado una vez acá y usado en cada string visible de más abajo.
  // Los console.log/console.error (solo para depurar, nunca se muestran
  // al usuario) se dejan en español a propósito, igual que en el resto
  // del código.
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  // Moneda default para cuentas nuevas y para destacar primero en el
  // Balance Total: la local del usuario (por país de perfil si ya cargó
  // desde Redux; si no, por idioma/zona horaria del navegador — ver
  // detectDefaultCurrency). Al ser un useMemo (no un useState fijado solo
  // al montar), se recalcula solo cuando llega el país real del perfil
  // (user.country llega después vía Redux) -- antes había un useState que
  // se congelaba en el primer render, cuando user todavía era null, y
  // nunca se volvía a sincronizar (por eso el Balance Total mostraba
  // "USD" por defecto aunque el cliente fuera de Perú).
  const defaultCurrency = useMemo(() => detectDefaultCurrency(user?.country), [user?.country]);


  // Bancos más importantes de América y Europa para sugerir en "Entidad
  // Bancaria". Es un Autocomplete con freeSolo, así que el cliente puede
  // escribir cualquier otro banco que no esté en la lista.
  const bancosSugeridos = [
    // Perú
    'Banco de Crédito del Perú (BCP)', 'BBVA Perú', 'Interbank', 'Scotiabank Perú',
    'Banco Pichincha', 'Banco de la Nación', 'MiBanco', 'Banco Falabella', 'Banco Ripley',
    // Latinoamérica
    'Bancolombia', 'Banco de Bogotá', 'Davivienda', 'BBVA México', 'Banorte', 'Santander México',
    'Citibanamex', 'Banco Estado (Chile)', 'Banco de Chile', 'Banco Santander Chile',
    'Itaú', 'Banco do Brasil', 'Bradesco', 'Caixa Econômica Federal', 'Banco Nación (Argentina)',
    'Banco Galicia', 'Banco Santander Río',
    // Norteamérica
    'JPMorgan Chase', 'Bank of America', 'Wells Fargo', 'Citibank', 'Goldman Sachs',
    'Morgan Stanley', 'U.S. Bank', 'Capital One', 'TD Bank', 'PNC Bank',
    'Royal Bank of Canada (RBC)', 'Scotiabank', 'BMO', 'CIBC',
    // Europa
    'HSBC', 'BNP Paribas', 'Deutsche Bank', 'Santander', 'BBVA', 'Crédit Agricole',
    'Société Générale', 'ING', 'UBS', 'Credit Suisse', 'Barclays', 'Lloyds Bank',
    'NatWest', 'UniCredit', 'Intesa Sanpaolo', 'Nordea', 'Banco de España',
    // Otro
    isSpanish ? 'Otro' : 'Other',
  ];

  // Taxonomía de "Tipo de Cuenta": dos categorías — Activos (plata
  // disponible) y Deuda (pasivos). El campo "tipo" en el backend sigue
  // siendo un solo string plano (Account.tipo); la categoría se deriva de
  // él acá mismo con esTipoDeuda(), no es una columna aparte.
  const TIPOS_ACTIVO = [
    { value: 'ahorros', label: isSpanish ? 'Cuenta de Ahorros' : 'Savings Account' },
    { value: 'corriente', label: isSpanish ? 'Cuenta Corriente' : 'Checking Account' },
  ];
  const TIPOS_DEUDA = [
    { value: 'tarjeta_credito', label: isSpanish ? 'Tarjeta de Crédito' : 'Credit Card' },
    { value: 'prestamo_personal', label: isSpanish ? 'Préstamo Personal' : 'Personal Loan' },
    { value: 'prestamo_hipotecario', label: isSpanish ? 'Préstamo Hipotecario' : 'Mortgage Loan' },
    { value: 'prestamo_auto', label: isSpanish ? 'Préstamo Vehicular' : 'Auto Loan' },
    { value: 'prestamo_educacion', label: isSpanish ? 'Préstamo Educativo' : 'Student Loan' },
    { value: 'prestamo_proveedores', label: isSpanish ? 'Préstamo a Proveedores' : 'Vendor Loan' },
    { value: 'otro', label: isSpanish ? 'Otro' : 'Other' },
  ];
  const esTipoDeuda = (tipo) => TIPOS_DEUDA.some((t) => t.value === tipo);


  // Lista de cuentas (vacía inicialmente)
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [migrationStatus, setMigrationStatus] = useState(null);

  // 21 sep 2026 (Augusto: "las Tarjetas de crédito no deberían estar como
  // cuentas sino como Deuda... Las cuentas sólo Ahorros y Cuenta
  // Corriente").
  //
  // Tenía razón: una tarjeta no es plata que tienes, es plata que debes, y
  // mostrarla junto a tus ahorros invita a leer mal el balance. Desde acá
  // Cuentas lista únicamente activos, y las tarjetas y préstamos se
  // administran en Deudas.
  //
  // Por dentro la tarjeta SIGUE existiendo como cuenta -- es donde se
  // cuelgan los consumos que importa el lector de estados de cuenta y el
  // otro lado de cada pago (ver transactions.py). Lo que cambia es que ya
  // no se ve ni se puede crear desde acá; no se borra nada.
  //
  // 21 sep 2026, segunda pasada (Augusto: "las tarjetas de crédito aún
  // aparecen en Cuentas"): el primer intento escondía lo que RECONOCÍA
  // como deuda, así que cualquier tarjeta con un `tipo` fuera de la lista
  // --datos viejos, una importación que lo guardó distinto-- se colaba.
  // La regla se invierte: se muestra solo lo que es una cuenta de verdad.
  // Lo que no calza no se ve acá, sin importar cómo esté escrito.
  //
  // Única excepción: una cuenta sin `tipo`. Es plata que alguien registró
  // antes de que este campo existiera, y hacerla desaparecer sería peor
  // que mostrarla de más.
  const cuentasVisibles = useMemo(
    () => cuentas.filter((c) => {
      const tipo = (c.tipo || '').trim().toLowerCase();
      if (!tipo) return true;
      return TIPOS_ACTIVO.some((t) => t.value === tipo);
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cuentas],
  );
  const cuentasDeDeudaOcultas = cuentas.length - cuentasVisibles.length;

  // 25 sep 2026 (Augusto: "no se han juntado las cuentas iguales").
  //
  // Tenía razón en esperar que PCM se lo señalara. El importador fabricó
  // las filas repetidas (ver numero_cuenta en statements.py) y después le
  // dejaba a él la tarea de descubrir cuáles eran la misma, mirando
  // movimientos de a una.
  //
  // Esto NO une nada solo: une mal es irreversible, y dos cuentas del
  // mismo banco con el mismo nombre pueden ser de verdad distintas (la de
  // ahorros y la de detracciones de SUNAT se llaman igual). Solo agrupa
  // las sospechosas y le pone el par delante para que decida en un clic.
  //
  // Sospechoso = mismo banco, mismo nombre, misma moneda, y ningún código
  // de cuenta que las contradiga. En cuanto una tenga código propio y la
  // otra tenga uno distinto, deja de sugerirse.
  const gruposSospechosos = useMemo(() => {
    const porClave = new Map();
    cuentasVisibles.forEach((c) => {
      const clave = [
        (c.entidad || '').trim().toLowerCase(),
        (c.nombre || '').trim().toLowerCase(),
        (c.moneda || '').trim().toUpperCase(),
      ].join('|');
      if (!porClave.has(clave)) porClave.set(clave, []);
      porClave.get(clave).push(c);
    });
    const soloDigitos = (v) => (v || '').replace(/\D/g, '');
    return [...porClave.values()].filter((grupo) => {
      if (grupo.length < 2) return false;
      // Si dos del grupo tienen códigos completos y distintos, son cuentas
      // distintas y no hay nada que sugerir.
      const codigos = grupo.map((c) => soloDigitos(c.numero_cuenta)).filter((v) => v.length >= 8);
      return new Set(codigos).size <= 1;
    });
  }, [cuentasVisibles]);

  // Cargar cuentas desde PostgreSQL y migrar datos si es necesario
  //
  // 5 ago 2026 (Augusto: "por qué una vez importada una nueva cuenta, no
  // actualiza las cuentas ingresadas? o el usuario tiene que hacer
  // refresh?"): sí -- fetchAccounts solo corría una vez, al montar esta
  // página (dependencias vacías más abajo). El diálogo de Importar vive
  // en el header (NotionHeader.js), montado aparte de esta página, así
  // que aunque el usuario tuviera Cuentas abierta al importar, no había
  // forma de que esta página se enterara del cambio -- se veía la
  // pantalla de "¡Importación completada!" pero la tabla de cuentas
  // seguía mostrando los datos viejos hasta un refresh manual del
  // navegador. Se saca fetchAccounts de adentro del efecto para poder
  // volver a llamarla desde un segundo efecto que escucha el evento
  // 'pcm:accounts-updated' (StatementImporter.js lo dispara justo
  // después de que la importación termina bien).
  const fetchAccounts = useCallback(async () => {
    try {
        setLoading(true);

        // Primero, verificamos si hay datos en localStorage que necesitan migración
        const migrationResult = await migrationService.migrateAccountsFromLocalStorage();
        setMigrationStatus(migrationResult);
        console.log('Resultado de migración:', migrationResult);

        // Ahora obtenemos las cuentas desde PostgreSQL
        const token = sessionStorage.getItem('token');
        if (!token) {
          throw new Error('No se encontró token de autenticación');
        }

        // FIX: esto armaba la URL a mano con axios + API_URL
        // (`${API_URL}/api/v1/dashboard/accounts`), y como API_URL en
        // producción ya incluye `/api/v1`, la URL real quedaba duplicada
        // (.../api/v1/api/v1/dashboard/accounts) -> 404 siempre. Además el
        // backend nuevo devuelve la lista de cuentas directamente (un
        // array), no envuelta en `{success, data}` como esperaba este
        // código, y el campo de saldo se llama `saldo` en el backend
        // (acá se sigue usando `balance` para no tocar el resto del
        // archivo).
        const response = await apiClient.get('/dashboard/accounts');
        const formattedAccounts = response.data.map(account => ({
          id: account.id,
          nombre: account.nombre,
          tipo: account.tipo,
          tipo_personalizado: account.tipo_personalizado || '',
          entidad: account.entidad,
          // 25 sep 2026: el backend siempre mandó los últimos dígitos (el
          // importador los extrae del extracto) pero este map los tiraba,
          // así que dos cuentas del mismo banco y la misma moneda se veían
          // idénticas en pantalla aunque fueran cuentas distintas.
          ultimos_digitos: account.ultimos_digitos || null,
          // 25 sep 2026: código de cuenta completo ("194-93394252-0-26").
          numero_cuenta: account.numero_cuenta || null,
          balance: parseFloat(account.saldo),
          moneda: account.moneda,
          // 28 ago 2026: día de cierre del ciclo de facturación (solo
          // tarjetas de crédito) -- ver TransactionsPage.js, agrupación
          // "Fecha de corte".
          dia_cierre: account.dia_cierre ?? null,
          // 4 sep 2026 (Augusto: cuentas de reserva/ahorro intocables) --
          // ver CalendarPage.js y TransactionsPage.js, donde se usa para
          // excluirlas por defecto de la vista agregada.
          es_reserva: account.es_reserva ?? false,
        }));

        console.log('Cuentas cargadas desde PostgreSQL:', formattedAccounts);
        setCuentas(formattedAccounts);
      } catch (err) {
        console.error('Error al cargar cuentas desde PostgreSQL:', err);
        setError(err.message);

        // Fallback a localStorage si la API falla
        try {
          console.log('Intentando cargar cuentas desde localStorage como fallback...');
          // Limpiar localStorage de datos antiguos
          localStorage.removeItem('demoCuentas');

          const financialData = localStorage.getItem('financialData');
          if (financialData) {
            const parsedData = JSON.parse(financialData);
            if (parsedData.newAccounts && parsedData.newAccounts.length > 0) {
              console.log('CuentasPage: Usando datos locales como fallback');
              setCuentas(parsedData.newAccounts);
            }
          }
        } catch (localError) {
          console.error('Error usando fallback local:', localError);
        }
      } finally {
        setLoading(false);
      }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // 10 ago 2026 (Augusto: "no aparece un indicador si va a ingresar más
  // cuentas o deudas o no"): mismo fix que en DebtsPage.js -- se lee el
  // estado de onboarding acá para poder confirmar, justo debajo de la
  // tabla, que este paso ya quedó listo (con la opción de seguir
  // agregando o continuar a Deudas), y refreshOnboarding() avisa también
  // a NotionLayout (vía OnboardingRefreshContext) para que el checklist
  // de la barra lateral se actualice sin depender de un cambio de ruta.
  const triggerOnboardingRefresh = useOnboardingRefresh();
  const [onboardingData, setOnboardingData] = useState(null);
  const refreshOnboarding = () => {
    onboardingService
      .getOnboardingData()
      .then((data) => setOnboardingData(data))
      .catch(() => {});
    triggerOnboardingRefresh();
  };
  useEffect(() => {
    refreshOnboarding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ver comentario arriba de fetchAccounts -- vuelve a pedir la lista de
  // cuentas cuando el Importador de Estados de Cuenta (StatementImporter.js)
  // avisa que terminó una importación, sin necesidad de refrescar la página.
  useEffect(() => {
    window.addEventListener('pcm:accounts-updated', fetchAccounts);
    return () => window.removeEventListener('pcm:accounts-updated', fetchAccounts);
  }, [fetchAccounts]);

  // Dialog states
  const [openNew, setOpenNew] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  // 25 sep 2026: un borrado que falla en el servidor tiene que verse.
  const [errorBorrado, setErrorBorrado] = useState(null);

  const [nuevaCuenta, setNuevaCuenta] = useState({
    nombre: '',
    tipo: 'corriente',
    tipo_personalizado: '',
    entidad: '',
    balance: 0,
    moneda: defaultCurrency,
    dia_cierre: '',
    // 4 sep 2026 (Augusto: cuentas de reserva/ahorro intocables) -- ver
    // el switch "Cuenta de reserva" en el diálogo de creación, más abajo.
    es_reserva: false,
  });

  // Estado para la cuenta que se está editando
  const [cuentaEditando, setCuentaEditando] = useState(null);

  // Handle dialogs open/close
  const handleOpenNew = () => setOpenNew(true);
  const handleCloseNew = () => setOpenNew(false);

  const handleOpenEdit = (cuenta) => {
    // El campo "Balance" del formulario siempre se llena con un número
    // positivo ("¿cuánto tienes/debes?"); el signo (negativo para deudas)
    // se aplica solo al guardar — ver handleActualizarCuenta. Si la cuenta
    // ya es una deuda, se muestra el valor absoluto para no confundir con
    // un "-" en el campo.
    setCuentaEditando({
      ...cuenta,
      balance: esTipoDeuda(cuenta.tipo) ? Math.abs(cuenta.balance) : cuenta.balance,
    });
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setCuentaEditando(null);
  };

  const handleDeleteConfirmOpen = (id) => {
    setDeleteId(id);
    setErrorBorrado(null);
    setOpenDelete(true);
  };

  const handleDeleteConfirmClose = () => {
    setOpenDelete(false);
    setDeleteId(null);
  };

  // 12 ago 2026 (pedido de Augusto): "seleccionar varias cuentas y
  // borrarlas juntas" + "modificar cada campo con doble clic" -- mismo
  // patrón que TransactionsPage.js. Sin endpoint de borrado masivo en el
  // backend, se manda un DELETE por cada id seleccionado.
  const [seleccionadas, setSeleccionadas] = useState(() => new Set());
  const [openBulkDelete, setOpenBulkDelete] = useState(false);
  // 25 sep 2026 (Augusto: "quiero borrar a cero, transacciones y
  // cuentas, menos los Presupuestos"). Reinicio completo del lado de los
  // DATOS, conservando la configuración. Vive acá, en Cuentas, porque es
  // acá donde uno mira cuando decide que quiere empezar de nuevo.
  const [openReinicio, setOpenReinicio] = useState(false);
  const [reinicioPreview, setReinicioPreview] = useState(null);
  const [reinicioTexto, setReinicioTexto] = useState('');
  const [reiniciando, setReiniciando] = useState(false);
  const [reinicioError, setReinicioError] = useState(null);
  // 25 sep 2026 (Augusto: "debería [haber] una opción para seleccionar
  // las cuentas o deudas que no se quiere borrar"). Ids marcados para
  // salvar; vacío = se borra todo.
  const [conservarCuentas, setConservarCuentas] = useState(new Set());
  const [conservarDeudas, setConservarDeudas] = useState(new Set());
  const alternarConservar = (setter) => (id) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const abrirReinicio = () => {
    setReinicioTexto('');
    setReinicioError(null);
    setReinicioPreview(null);
    setConservarCuentas(new Set());
    setConservarDeudas(new Set());
    setOpenReinicio(true);
    apiClient.get('/transactions/reiniciar-datos/preview')
      .then((res) => setReinicioPreview(res.data))
      .catch(() => setReinicioError(isSpanish ? 'No se pudo consultar qué se borraría.' : 'Could not check what would be deleted.'));
  };

  const confirmarReinicio = async () => {
    setReiniciando(true);
    setReinicioError(null);
    try {
      await apiClient.post('/transactions/reiniciar-datos', {
        confirmacion: reinicioTexto,
        transacciones_esperadas: reinicioPreview?.se_borra?.transacciones ?? null,
        conservar_account_ids: [...conservarCuentas],
        conservar_debt_ids: [...conservarDeudas],
      });
      setOpenReinicio(false);
      window.location.reload();
    } catch (err) {
      setReinicioError(err.response?.data?.detail || (isSpanish ? 'No se pudo reiniciar.' : 'Could not reset.'));
    } finally {
      setReiniciando(false);
    }
  };
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // 28 ago 2026 (pedido de Augusto: "eso tiene que ser extremadamente
  // cuidadoso" -- el borrado masivo es definitivo y se lleva también
  // todas las transacciones de cada cuenta, sin papelera): ya no basta
  // con un solo click en "Eliminar" -- hay que escribir el número de
  // cuentas seleccionadas (o "ELIMINAR") para habilitar el botón.
  const [bulkDeleteConfirmText, setBulkDeleteConfirmText] = useState('');
  const [editingCell, setEditingCell] = useState(null); // { id, field }
  const [editValue, setEditValue] = useState('');

  const toggleSeleccion = (id) => {
    setSeleccionadas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // Sobre cuentasVisibles, no sobre todas: "seleccionar todo" no puede
  // marcar una tarjeta que ni siquiera está en pantalla -- y menos cuando
  // lo que sigue es un borrado masivo que se lleva sus movimientos.
  const todasSeleccionadas = cuentasVisibles.length > 0 && cuentasVisibles.every((c) => seleccionadas.has(c.id));
  const algunaSeleccionada = cuentasVisibles.some((c) => seleccionadas.has(c.id));
  const toggleSeleccionarTodas = () => {
    setSeleccionadas((prev) => {
      const next = new Set(prev);
      if (todasSeleccionadas) cuentasVisibles.forEach((c) => next.delete(c.id));
      else cuentasVisibles.forEach((c) => next.add(c.id));
      return next;
    });
  };
  const handleBulkDeleteOpen = () => {
    setBulkDeleteConfirmText('');
    setOpenBulkDelete(true);
  };
  const handleBulkDeleteClose = () => setOpenBulkDelete(false);
  // 17 sep 2026: acepta la palabra de confirmación en español O en
  // inglés (ver el placeholder/ayuda del diálogo, que ahora muestra una u
  // otra según isSpanish) -- si no, un usuario viendo la pantalla en
  // inglés escribiría "DELETE" y el botón se quedaría deshabilitado
  // igual, porque este chequeo solo reconocía "ELIMINAR".
  const bulkDeleteConfirmed =
    bulkDeleteConfirmText.trim().toUpperCase() === 'ELIMINAR' ||
    bulkDeleteConfirmText.trim().toUpperCase() === 'DELETE' ||
    bulkDeleteConfirmText.trim() === String(seleccionadas.size);
  const handleBulkDelete = async () => {
    if (!bulkDeleteConfirmed) return;
    setBulkDeleting(true);
    try {
      await Promise.all([...seleccionadas].map((id) => deleteAccountFromPostgreSQL(id)));
      setCuentas((prev) => prev.filter((c) => !seleccionadas.has(c.id)));
    } finally {
      setBulkDeleting(false);
      setSeleccionadas(new Set());
      setBulkDeleteConfirmText('');
      setOpenBulkDelete(false);
    }
  };

  // 25 sep 2026 (Augusto, viendo tres filas idénticas "Cuenta Digital BCP
  // SOLES"): unir dos cuentas seleccionadas. Se elige cuál sobrevive
  // porque no siempre es obvio -- la que tiene el nombre corregido a mano,
  // o la que ya está usada en presupuestos, puede ser cualquiera de las dos.
  const [openUnir, setOpenUnir] = useState(false);
  const [uniendo, setUniendo] = useState(false);
  const [destinoUnion, setDestinoUnion] = useState(null);
  const [errorUnion, setErrorUnion] = useState(null);
  const cuentasSeleccionadas = cuentasVisibles.filter((c) => seleccionadas.has(c.id));
  const puedeUnir =
    cuentasSeleccionadas.length === 2 &&
    (cuentasSeleccionadas[0].moneda || '').toUpperCase() ===
      (cuentasSeleccionadas[1].moneda || '').toUpperCase();
  // Marca el par sugerido y abre el diálogo de unión en un solo clic.
  const revisarGrupo = (grupo) => {
    const [a, b] = grupo;
    setSeleccionadas(new Set([a.id, b.id]));
    setDestinoUnion(!a.numero_cuenta && b.numero_cuenta ? b.id : a.id);
    setErrorUnion(null);
    setOpenUnir(true);
  };
  const handleUnirOpen = () => {
    // Por defecto sobrevive la que tiene más información identificatoria
    // (últimos dígitos), y si empatan, la primera.
    const [a, b] = cuentasSeleccionadas;
    setDestinoUnion(!a.ultimos_digitos && b.ultimos_digitos ? b.id : a.id);
    setErrorUnion(null);
    setOpenUnir(true);
  };
  const handleUnir = async () => {
    if (!puedeUnir || !destinoUnion) return;
    const origen = cuentasSeleccionadas.find((c) => c.id !== destinoUnion);
    if (!origen) return;
    setUniendo(true);
    setErrorUnion(null);
    try {
      const data = await unirCuentas(origen.id, destinoUnion);
      const actualizada = data.cuenta || {};
      setCuentas((prev) =>
        prev
          .filter((c) => c.id !== origen.id)
          .map((c) =>
            c.id === destinoUnion
              ? {
                  ...c,
                  // El backend habla en `saldo`; esta pantalla, en
                  // `balance` (ver el map de carga más arriba).
                  balance:
                    actualizada.saldo !== undefined && actualizada.saldo !== null
                      ? parseFloat(actualizada.saldo)
                      : c.balance,
                  ultimos_digitos: actualizada.ultimos_digitos ?? c.ultimos_digitos,
                  numero_cuenta: actualizada.numero_cuenta ?? c.numero_cuenta,
                  entidad: actualizada.entidad ?? c.entidad,
                  dia_cierre: actualizada.dia_cierre ?? c.dia_cierre,
                }
              : c
          )
      );
      setSeleccionadas(new Set());
      setOpenUnir(false);
    } catch (error) {
      setErrorUnion(
        error.response?.data?.detail ||
          (isSpanish ? 'No se pudieron unir las cuentas.' : 'Could not merge the accounts.')
      );
    } finally {
      setUniendo(false);
    }
  };

  // Handle input changes for new account
  const handleChange = (e) => {
    const { name, value } = e.target;
    setNuevaCuenta({
      ...nuevaCuenta,
      [name]: name === 'balance' ? parseFloat(value) : name === 'dia_cierre' ? (value === '' ? '' : parseInt(value, 10)) : value,
      // Si cambia a un tipo distinto de "otro", no tiene sentido conservar
      // un tipo_personalizado viejo.
      ...(name === 'tipo' && value !== 'otro' ? { tipo_personalizado: '' } : {}),
    });
  };

  // Handle input changes for editing account
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setCuentaEditando({
      ...cuentaEditando,
      [name]: name === 'balance' ? parseFloat(value) : name === 'dia_cierre' ? (value === '' ? '' : parseInt(value, 10)) : value,
      ...(name === 'tipo' && value !== 'otro' ? { tipo_personalizado: '' } : {}),
    });
  };

  // Indicadores de estado para operaciones
  const [isSaving, setIsSaving] = useState(false);
  const [operationError, setOperationError] = useState(null);

  // Función para guardar cuentas en PostgreSQL
  const saveAccountToPostgreSQL = async (account) => {
    try {
      setIsSaving(true);
      setOperationError(null);

      // El backend espera `saldo`, no `balance` (y no envuelve la
      // respuesta en `{success, data}` — devuelve la cuenta creada tal
      // cual).
      const accountData = {
        nombre: account.nombre,
        tipo: account.tipo,
        tipo_personalizado: account.tipo_personalizado || null,
        entidad: account.entidad,
        saldo: account.balance,
        moneda: account.moneda,
        dia_cierre: account.dia_cierre === '' || account.dia_cierre === undefined ? null : account.dia_cierre,
        es_reserva: !!account.es_reserva,
        // 21 sep 2026 (Augusto: "aparece como una cuenta... y no en la
        // zona de Deudas"): al crear una cuenta de tipo deuda, el backend
        // crea además su entrada en Deudas. Estos tres campos son lo que
        // esa sección necesita para servir de algo -- cuota, costo de
        // financiamiento y alertas de vencimiento. Todos opcionales:
        // exigir la tasa para registrar un préstamo familiar sería
        // inventar un requisito.
        tasa_interes: account.tasa_interes === '' || account.tasa_interes === undefined
          ? null : Number(account.tasa_interes),
        pago_mensual: account.pago_mensual === '' || account.pago_mensual === undefined
          ? null : Number(account.pago_mensual),
        dia_vencimiento: account.dia_vencimiento === '' || account.dia_vencimiento === undefined
          ? null : Number(account.dia_vencimiento),
      };

      const response = await apiClient.post('/dashboard/accounts', accountData);
      console.log('Cuenta guardada exitosamente en PostgreSQL:', response.data);
      return {
        id: response.data.id,
        nombre: response.data.nombre,
        tipo: response.data.tipo,
        tipo_personalizado: response.data.tipo_personalizado || '',
        entidad: response.data.entidad,
        balance: parseFloat(response.data.saldo),
        moneda: response.data.moneda,
        dia_cierre: response.data.dia_cierre ?? null,
        es_reserva: response.data.es_reserva ?? false,
      };
    } catch (error) {
      console.error('Error guardando cuenta en PostgreSQL:', error);
      setOperationError(error.message);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // Función para actualizar cuenta en PostgreSQL
  const updateAccountInPostgreSQL = async (accountId, accountData) => {
    try {
      setIsSaving(true);
      setOperationError(null);

      // El backend espera `saldo`, no `balance`.
      const payload = { ...accountData };
      if ('balance' in payload) {
        payload.saldo = payload.balance;
        delete payload.balance;
      }

      const response = await apiClient.put(`/dashboard/accounts/${accountId}`, payload);
      console.log('Cuenta actualizada exitosamente en PostgreSQL:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error actualizando cuenta en PostgreSQL:', error);
      setOperationError(error.message);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // Función para eliminar cuenta en PostgreSQL
  const deleteAccountFromPostgreSQL = async (accountId) => {
    try {
      setIsSaving(true);
      setOperationError(null);

      const response = await apiClient.delete(`/dashboard/accounts/${accountId}`);
      console.log('Cuenta eliminada exitosamente en PostgreSQL:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error eliminando cuenta en PostgreSQL:', error);
      setOperationError(error.message);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // 25 sep 2026: unir dos cuentas que son la misma partida en dos filas
  // (ver unir_cuentas en dashboard.py). No borra nada: los movimientos, los
  // saldos de extracto y las conciliaciones pasan a la cuenta que queda.
  const unirCuentas = async (origenId, destinoId) => {
    const response = await apiClient.post(`/dashboard/accounts/${origenId}/unir`, {
      destino_id: destinoId,
    });
    return response.data;
  };

  // Mantener la compatibilidad con localStorage como fallback
  const saveAccountsToStorage = (updatedAccounts) => {
    try {
      // Guardar en financialData para mantener sincronización con onboarding
      const financialData = localStorage.getItem('financialData');

      if (financialData) {
        const parsedData = JSON.parse(financialData);
        parsedData.newAccounts = updatedAccounts;
        localStorage.setItem('financialData', JSON.stringify(parsedData));
        console.log('Fallback: Cuentas guardadas en localStorage');
      } else {
        // Si no existe financialData, crearlo
        const newData = { newAccounts: updatedAccounts };
        localStorage.setItem('financialData', JSON.stringify(newData));
      }
    } catch (error) {
      console.error('Error guardando cuentas en localStorage:', error);
    }
  };

  // Manejar la creación de una nueva cuenta
  const handleCreateAccount = async (newAccountInput) => {
    // El campo "Balance" del formulario siempre se llena en positivo
    // ("¿cuánto tienes/debes?"); para una Deuda se guarda en negativo para
    // que reste del Balance Total en vez de sumar como plata disponible —
    // mismo criterio que ya se usa para tarjetas importadas de un estado
    // de cuenta (ver backend/statements.py).
    const balanceFinal = esTipoDeuda(newAccountInput.tipo)
      ? -Math.abs(Number(newAccountInput.balance) || 0)
      : Number(newAccountInput.balance) || 0;
    const newAccount = { ...newAccountInput, balance: balanceFinal };

    try {
      // Guardar en PostgreSQL
      const createdAccount = await saveAccountToPostgreSQL(newAccount);

      // Añadir la cuenta creada al estado local
      const formattedAccount = {
        id: createdAccount.id,
        nombre: createdAccount.nombre || newAccount.nombre,
        tipo: createdAccount.tipo || newAccount.tipo,
        tipo_personalizado: createdAccount.tipo_personalizado || newAccount.tipo_personalizado || '',
        entidad: createdAccount.entidad || newAccount.entidad,
        balance: parseFloat(createdAccount.balance ?? newAccount.balance),
        moneda: createdAccount.moneda || newAccount.moneda,
        dia_cierre: createdAccount.dia_cierre ?? newAccount.dia_cierre ?? null,
        es_reserva: createdAccount.es_reserva ?? newAccount.es_reserva ?? false,
      };

      const updatedAccounts = [...cuentas, formattedAccount];

      // Actualizar estado
      setCuentas(updatedAccounts);

      // Guardar en localStorage como fallback
      saveAccountsToStorage(updatedAccounts);
    } catch (error) {
      console.error('Error al crear cuenta:', error);

      // Si falla la API, intentar guardar solo en localStorage
      try {
        const fallbackAccount = {
          ...newAccount,
          id: cuentas.length + 1,
        };
        const updatedAccounts = [...cuentas, fallbackAccount];
        saveAccountsToStorage(updatedAccounts);
        setCuentas(updatedAccounts);
      } catch (localError) {
        console.error('Error en fallback local:', localError);
      }
    } finally {
      // Reiniciar el formulario y cerrar el diálogo siempre, independientemente del resultado
      setNuevaCuenta({
        nombre: '',
        tipo: 'corriente',
        tipo_personalizado: '',
        entidad: '',
        balance: 0,
        moneda: defaultCurrency,
        dia_cierre: '',
        es_reserva: false,
      });
      handleCloseNew();
      // Ver comentario junto a refreshOnboarding más arriba.
      refreshOnboarding();
    }
  };

  // Update existing account
  const handleActualizarCuenta = async () => {
    // Mismo criterio que en handleCreateAccount: el campo "Balance" del
    // formulario se llena en positivo, y para una Deuda se persiste en
    // negativo.
    const balanceFinal = esTipoDeuda(cuentaEditando.tipo)
      ? -Math.abs(Number(cuentaEditando.balance) || 0)
      : Number(cuentaEditando.balance) || 0;
    const cuentaActualizada = { ...cuentaEditando, balance: balanceFinal };

    try {
      // Actualizar en PostgreSQL
      const accountData = {
        nombre: cuentaActualizada.nombre,
        tipo: cuentaActualizada.tipo,
        tipo_personalizado: cuentaActualizada.tipo_personalizado || null,
        entidad: cuentaActualizada.entidad,
        balance: cuentaActualizada.balance,
        moneda: cuentaActualizada.moneda,
        dia_cierre: cuentaActualizada.dia_cierre === '' || cuentaActualizada.dia_cierre === undefined ? null : cuentaActualizada.dia_cierre,
        es_reserva: !!cuentaActualizada.es_reserva,
      };

      await updateAccountInPostgreSQL(cuentaActualizada.id, accountData);

      // Actualizar estado local
      setCuentas(cuentas.map(c =>
        c.id === cuentaActualizada.id ? cuentaActualizada : c
      ));

      // Actualizar localStorage como fallback
      const updatedAccounts = cuentas.map(c =>
        c.id === cuentaActualizada.id ? cuentaActualizada : c
      );
      saveAccountsToStorage(updatedAccounts);

      console.log('Cuenta actualizada correctamente');
    } catch (error) {
      console.error('Error al actualizar cuenta:', error);

      // Si falla la API, intentar actualizar solo en localStorage
      try {
        const updatedAccounts = cuentas.map(c =>
          c.id === cuentaActualizada.id ? cuentaActualizada : c
        );
        saveAccountsToStorage(updatedAccounts);
        setCuentas(updatedAccounts);
      } catch (localError) {
        console.error('Error en fallback local:', localError);
      }
    } finally {
      handleCloseEdit();
    }
  };

  // Delete an account
  const handleEliminarCuenta = async () => {
    if (deleteId) {
      try {
        // Eliminar de PostgreSQL
        await deleteAccountFromPostgreSQL(deleteId);

        const updatedAccounts = cuentas.filter(c => c.id !== deleteId);

        // Actualizar estado
        setCuentas(updatedAccounts);

        // Actualizar localStorage como fallback
        saveAccountsToStorage(updatedAccounts);

        console.log('Cuenta eliminada correctamente');
        handleDeleteConfirmClose();
      } catch (error) {
        // 25 sep 2026 (Augusto: "cuando borré las cuentas bancarias y las
        // volví a ingresar me duplicaron las transacciones").
        //
        // Acá estaba la mentira. Si el borrado fallaba en el servidor,
        // este bloque sacaba la cuenta de la pantalla igual y la borraba
        // del localStorage. El usuario la veía desaparecer y creía que se
        // había ido -- pero seguía viva en el servidor, con todos sus
        // movimientos. Al reimportar el extracto, los movimientos se
        // sumaban a los que nunca se fueron.
        //
        // Un borrado que falla tiene que decir que falló. Nada de "borrar"
        // solo en la pantalla.
        console.error('Error al eliminar cuenta:', error);
        setErrorBorrado(
          error.response?.data?.detail ||
            (isSpanish
              ? 'No se pudo eliminar la cuenta en el servidor. La cuenta y sus movimientos siguen ahí — no la vuelvas a importar hasta resolver esto.'
              : 'The account could not be deleted on the server. It and its transactions are still there — do not re-import it until this is resolved.')
        );
      }
    }
  };

  // 12 ago 2026: edición inline con doble clic. Se manda solo el campo
  // tocado -- updateAccountInPostgreSQL ya sabe mapear balance -> saldo
  // (ver arriba), y el backend acepta updates parciales (ver
  // endpoints/dashboard.py, exclude_unset=True).
  // 25 sep 2026: numero_cuenta entra acá para que el usuario pueda
  // escribir el código de su cuenta sin re-importar el extracto (las
  // cuentas creadas antes de este cambio no lo tienen).
  const CAMPOS_EDITABLES_INLINE = ['nombre', 'tipo', 'tipo_personalizado', 'entidad', 'balance', 'numero_cuenta'];
  const startEdit = (cuenta, field) => {
    if (!CAMPOS_EDITABLES_INLINE.includes(field)) return;
    setEditingCell({ id: cuenta.id, field });
    if (field === 'balance') {
      setEditValue(esTipoDeuda(cuenta.tipo) ? Math.abs(cuenta.balance) : cuenta.balance);
    } else {
      setEditValue(cuenta[field] ?? '');
    }
  };
  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };
  const commitEdit = async () => {
    if (!editingCell) return;
    const { id, field } = editingCell;
    const cuentaActual = cuentas.find((c) => c.id === id);
    if (!cuentaActual) { cancelEdit(); return; }

    const payload = {};
    if (field === 'balance') {
      const monto = parseFloat(editValue) || 0;
      payload.balance = esTipoDeuda(cuentaActual.tipo) ? -Math.abs(monto) : monto;
    } else if (field === 'tipo') {
      payload.tipo = editValue;
      // Si el nuevo tipo cruza entre Activo <-> Deuda, se re-firma el
      // balance para que siga sumando/restando bien en el Balance Total
      // -- mismo criterio que handleActualizarCuenta (formulario completo).
      const eraDeuda = esTipoDeuda(cuentaActual.tipo);
      const esDeudaAhora = esTipoDeuda(editValue);
      if (eraDeuda !== esDeudaAhora) {
        const absoluto = Math.abs(cuentaActual.balance);
        payload.balance = esDeudaAhora ? -absoluto : absoluto;
      }
      if (editValue !== 'otro') payload.tipo_personalizado = '';
    } else {
      payload[field] = editValue;
    }

    try {
      const updated = await updateAccountInPostgreSQL(id, payload);
      setCuentas((prev) => prev.map((c) => (
        c.id === id
          ? {
              ...c,
              nombre: updated.nombre ?? c.nombre,
              tipo: updated.tipo ?? c.tipo,
              tipo_personalizado: updated.tipo_personalizado ?? '',
              entidad: updated.entidad ?? c.entidad,
              numero_cuenta: updated.numero_cuenta ?? c.numero_cuenta,
              balance: updated.saldo !== undefined && updated.saldo !== null ? parseFloat(updated.saldo) : c.balance,
              moneda: updated.moneda ?? c.moneda,
            }
          : c
      )));
    } finally {
      cancelEdit();
    }
  };
  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') cancelEdit();
  };

  // (Históricamente hubo acá un efecto roto que fijaba una "moneda base"
  // leyendo datos de ejemplo hardcodeados; se quitó junto con todo el
  // concepto de "moneda base" — ver resumenPorMoneda y defaultCurrency
  // más arriba, que reemplazan ese enfoque.)

  // Efecto para manejar las cuentas bancarias añadidas desde AddBankAccountForm
  useEffect(() => {
    // Verificar si hay cuentas bancarias en el estado de location
    if (location.state && location.state.newAccounts) {
      // Obtener las cuentas nuevas del estado
      const newAccounts = location.state.newAccounts;

      // Actualizar el ID de cada cuenta para evitar duplicados
      const maxId = cuentas.length > 0 ? Math.max(...cuentas.map(c => c.id)) : 0;
      const accountsWithUpdatedIds = newAccounts.map((account, index) => ({
        ...account,
        id: maxId + index + 1
      }));

      // Combinar las cuentas existentes con las nuevas
      setCuentas([...cuentas, ...accountsWithUpdatedIds]);

      // Limpiar el state para evitar duplicación en caso de recargas
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // 3 ago 2026 (pedido de Augusto, tercera vuelta): defaultCurrency (por
  // país de perfil / navegador / zona horaria) puede fallar en detectar
  // bien la ubicación real (ej. si el perfil no tiene país cargado, o el
  // navegador/entorno no da pistas claras) -- eso causó que la columna
  // "local" terminara mostrando "USD" aunque el cliente sea de Perú y
  // todas sus cuentas reales estén en soles. Como respaldo, si la
  // detección da USD pero el cliente ya tiene cuentas reales en otra
  // moneda, se usa la moneda más común entre sus cuentas como "moneda
  // local" en vez de confiar ciegamente en la detección automática.
  const monedaLocal = useMemo(() => {
    if (defaultCurrency !== 'USD') return defaultCurrency;
    const conteo = {};
    cuentas.forEach((c) => {
      if (c.moneda && c.moneda !== 'USD') conteo[c.moneda] = (conteo[c.moneda] || 0) + 1;
    });
    const candidatas = Object.keys(conteo).sort((a, b) => conteo[b] - conteo[a]);
    return candidatas[0] || 'USD';
  }, [defaultCurrency, cuentas]);

  // Moneda "secundaria" de referencia para la segunda columna: USD
  // siempre, salvo que la moneda local YA sea USD (cliente fuera de
  // Perú, por ejemplo), en cuyo caso la secundaria es EUR para no
  // repetir la misma columna dos veces.
  const monedaSecundaria = monedaLocal === 'USD' ? 'EUR' : 'USD';

  // 3 ago 2026 (pedido de Augusto): el Balance Total antes convertía todo
  // a una sola moneda con tasas de cambio fijas/inventadas (ej. mostrar
  // "$730.51 USD" cuando en realidad eran soles, o etiquetar esto como un
  // "Desglose" aparte más abajo) -- eso se quitó por completo. Ahora se
  // calculan directamente, SIN conversión, los totales reales de
  // Activos/Deuda/Neto de cada moneda que el cliente realmente tiene en
  // sus cuentas, y eso ES el Balance Total. La moneda local del cliente y
  // USD como referencia universal siempre aparecen primero, en S/ 0.00 o
  // $ 0.00 si el cliente todavía no tiene cuentas en esa moneda.
  const resumenPorMoneda = useMemo(() => {
    const monedas = new Set(cuentas.map((c) => c.moneda));
    monedas.add(monedaLocal);
    monedas.add(monedaSecundaria);
    const ordenadas = [...monedas].sort((a, b) => {
      const prioridad = (m) => (m === monedaLocal ? 0 : m === monedaSecundaria ? 1 : 2);
      return prioridad(a) - prioridad(b) || a.localeCompare(b);
    });
    return ordenadas.map((moneda) => {
      const cuentasDeEstaMoneda = cuentas.filter((c) => c.moneda === moneda);
      const activos = cuentasDeEstaMoneda
        .filter((c) => !esTipoDeuda(c.tipo))
        .reduce((t, c) => t + c.balance, 0);
      const deuda = cuentasDeEstaMoneda
        .filter((c) => esTipoDeuda(c.tipo))
        .reduce((t, c) => t + c.balance, 0);
      return { moneda, activos, deuda, neto: activos + deuda };
    });
  }, [cuentas, monedaLocal, monedaSecundaria]);

  // Texto legible para el tipo de cuenta — cubre las 2 cuentas de Activos
  // (ahorros/corriente) y las 7 de Deuda (tarjeta, préstamos, otro). Si el
  // tipo es "otro" y el usuario puso un nombre propio (tipo_personalizado),
  // se muestra ese en vez del genérico "Otro".
  const getTipoLabel = (cuenta) => {
    if (cuenta.tipo === 'otro' && cuenta.tipo_personalizado) return cuenta.tipo_personalizado;
    const encontrado = [...TIPOS_ACTIVO, ...TIPOS_DEUDA].find((t) => t.value === cuenta.tipo);
    if (encontrado) return encontrado.label;
    // Compatibilidad con datos viejos que no calzan en la lista.
    if (cuenta.tipo === 'corriente') return isSpanish ? 'Corriente' : 'Checking';
    return isSpanish ? 'Ahorros' : 'Savings';
  };

  // Obtener el símbolo de moneda
  const getMonedaSymbol = (monedaCode) => {
    switch(monedaCode) {
      case 'EUR': return '€';
      case 'USD': case 'MXN': case 'COP': case 'ARS': case 'CLP': case 'CAD': case 'AUD': return '$';
      case 'GBP': return '£';
      case 'JPY': case 'CNY': return '¥';
      case 'PEN': return 'S/';
      case 'BRL': return 'R$';
      default: return '';
    }
  };

  // 3 ago 2026 (pedido de Augusto): en la tabla de cuentas ya no hace
  // falta una columna "Moneda" aparte (con el código en un Chip) -- en
  // vez de eso hay SIEMPRE dos columnas fijas (moneda local y dólares,
  // ver monedaLocal/monedaSecundaria más arriba), y cada cuenta muestra
  // su balance directamente en la columna de su propia moneda y "—" en
  // la otra, sin convertir nada.
  const renderCeldaBalance = (cuenta, monedaColumna) => {
    if (cuenta.moneda === monedaColumna) {
      return (
        <Typography
          variant="body2"
          sx={{ fontWeight: 'bold', color: cuenta.balance >= 0 ? 'success.main' : 'error.main' }}
        >
          {getMonedaSymbol(monedaColumna)} {formatAmount(cuenta.balance)}
        </Typography>
      );
    }
    // Caso raro: una cuenta en una tercera moneda que no es ni la local
    // ni la secundaria (dólares) -- se muestra igual en la columna local
    // (con su propio código) para no perder el dato, en vez de
    // desaparecer.
    if (monedaColumna === monedaLocal && cuenta.moneda !== monedaSecundaria) {
      return (
        <Typography
          variant="body2"
          sx={{ fontWeight: 'bold', color: cuenta.balance >= 0 ? 'success.main' : 'error.main' }}
        >
          {getMonedaSymbol(cuenta.moneda)} {formatAmount(cuenta.balance)} {cuenta.moneda}
        </Typography>
      );
    }
    return (
      <Typography variant="body2" color="text.disabled">—</Typography>
    );
  };

  // Solo se puede editar el balance desde la columna que de verdad
  // muestra el valor real de esta cuenta (no desde la columna "—") --
  // mismos dos casos que ya distingue renderCeldaBalance de arriba.
  const puedeEditarBalanceAqui = (cuenta, monedaColumna) =>
    cuenta.moneda === monedaColumna || (monedaColumna === monedaLocal && cuenta.moneda !== monedaSecundaria);

  const renderCeldaBalanceEditable = (cuenta, monedaColumna) => {
    if (editingCell?.id === cuenta.id && editingCell?.field === 'balance') {
      return (
        <TextField
          autoFocus
          size="small"
          type="number"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleEditKeyDown}
          sx={{ maxWidth: 120 }}
        />
      );
    }
    return renderCeldaBalance(cuenta, monedaColumna);
  };

  // 17 sep 2026: nombres legibles de moneda para los <Select> de
  // moneda (creación y edición) -- antes venían fijos en español
  // ("Dólar Americano", "Sol Peruano", etc.), acá se traducen igual que
  // el resto de esta pantalla.
  const nombreMoneda = (codigo) => {
    const nombres = {
      USD: isSpanish ? 'Dólar Americano' : 'US Dollar',
      EUR: isSpanish ? 'Euro' : 'Euro',
      GBP: isSpanish ? 'Libra Esterlina' : 'British Pound',
      MXN: isSpanish ? 'Peso Mexicano' : 'Mexican Peso',
      COP: isSpanish ? 'Peso Colombiano' : 'Colombian Peso',
      ARS: isSpanish ? 'Peso Argentino' : 'Argentine Peso',
      CLP: isSpanish ? 'Peso Chileno' : 'Chilean Peso',
      PEN: isSpanish ? 'Sol Peruano' : 'Peruvian Sol',
      BRL: isSpanish ? 'Real Brasileño' : 'Brazilian Real',
      JPY: isSpanish ? 'Yen Japonés' : 'Japanese Yen',
      CNY: isSpanish ? 'Yuan Chino' : 'Chinese Yuan',
      CAD: isSpanish ? 'Dólar Canadiense' : 'Canadian Dollar',
      AUD: isSpanish ? 'Dólar Australiano' : 'Australian Dollar',
    };
    return `${nombres[codigo] || codigo} (${getMonedaSymbol(codigo)})`;
  };

  return (
    <Container maxWidth="md" sx={{ pt: 4, pb: 8 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {isSpanish ? 'Cuentas' : 'Accounts'}
      </Typography>

      {/* Mostrar indicador de carga */}
      {loading && (
        <Box sx={{ my: 2, display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            {isSpanish ? 'Cargando cuentas...' : 'Loading accounts...'}
          </Typography>
          <div className="loading-spinner" style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #f3f3f3', borderTop: '2px solid #3498db', animation: 'spin 1s linear infinite' }}></div>
        </Box>
      )}

      {/* Mostrar mensaje de error si hay alguno */}
      {error && (
        <Box sx={{ my: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography variant="body2" color="error.main">
            {isSpanish ? `Error al cargar datos: ${error}. Usando datos locales como respaldo.` : `Error loading data: ${error}. Using local data as a fallback.`}
          </Typography>
        </Box>
      )}

      {/* Mostrar mensaje de migración si se realizó */}
      {migrationStatus && migrationStatus.success && migrationStatus.results && (
        <Box sx={{ my: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
          <Typography variant="body2" color="success.dark">
            {migrationStatus.message} ({isSpanish ? 'Migradas' : 'Migrated'}: {migrationStatus.results.filter(r => r.success).length})
          </Typography>
        </Box>
      )}

      {/* Resumen del Balance Total.
          3 ago 2026 (pedido de Augusto, segunda vuelta): antes esto era un
          único número grande convertido a una moneda elegida en un
          selector (con tasas de cambio inventadas), y más abajo aparte un
          "Desglose por moneda" con los totales reales. Se fusionó todo en
          una sola cosa: el Balance Total ES directamente el total real
          (sin conversión) de cada moneda que el cliente tiene, con la
          moneda de su ubicación destacada primero — ya no hay selector ni
          etiqueta de "desglose". */}
      <Card sx={{ mb: 4, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h6" gutterBottom>
                {isSpanish ? 'Balance Total' : 'Total Balance'}
              </Typography>
              <Typography variant="body2">
                {isSpanish ? 'Total de todas tus cuentas' : 'Total of all your accounts'}
              </Typography>
            </Box>

            {/* 3 ago 2026 (pedido de Augusto): las tarjetas de moneda ya no
                ocupan todo el ancho en una Grid (dejaban mucho espacio en
                blanco) -- ahora son compactas (solo tan anchas como su
                contenido) y se alinean a la derecha, centradas junto al
                título, para ahorrar espacio. */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
              }}
            >
              {resumenPorMoneda.map(({ moneda, activos, deuda, neto }, idx) => (
                <Box
                  key={moneda}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.9)',
                    borderRadius: 1,
                    p: 1.5,
                    color: 'text.primary',
                    minWidth: 180,
                    ...(idx === 0 && { border: '2px solid rgba(255,255,255,0.9)' }),
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {getMonedaSymbol(moneda)} {moneda}
                  </Typography>
                  <Typography variant={idx === 0 ? 'h5' : 'h6'} sx={{ fontWeight: 'bold' }}>
                    {getMonedaSymbol(moneda)} {formatAmount(neto)}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      label={`${isSpanish ? 'Activos' : 'Assets'}: ${getMonedaSymbol(moneda)} ${formatAmount(activos)}`}
                    />
                    <Chip
                      size="small"
                      label={`${isSpanish ? 'Deuda' : 'Debt'}: ${getMonedaSymbol(moneda)} ${formatAmount(Math.abs(deuda))}`}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Botón para agregar nueva cuenta y contador */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleOpenNew}
          >
            {isSpanish ? 'Agregar Nueva Cuenta' : 'Add New Account'}
          </Button>
          {/* 25 sep 2026: empezar de cero, de verdad. Separado del
              borrado masivo de arriba porque hace algo distinto: ese
              borra cuentas elegidas, este deja el lado de los datos en
              cero para volver a importar todo. */}
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={abrirReinicio}
          >
            {isSpanish ? 'Empezar de cero' : 'Start over'}
          </Button>
          {/* 12 ago 2026 (pedido de Augusto): borrado masivo -- solo
              aparece cuando hay algo seleccionado. */}
          {seleccionadas.size > 0 && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon fontSize="small" />}
              onClick={handleBulkDeleteOpen}
            >
              {isSpanish ? `Eliminar seleccionadas (${seleccionadas.size})` : `Delete selected (${seleccionadas.size})`}
            </Button>
          )}
          {/* 25 sep 2026: aparece solo con exactamente 2 marcadas y de la
              misma moneda -- unir tres de golpe es más fácil de hacer mal
              que de deshacer, y de a dos se ve lo que pasa. */}
          {puedeUnir && (
            <Button variant="outlined" size="small" onClick={handleUnirOpen}>
              {isSpanish ? 'Unir las 2 seleccionadas' : 'Merge the 2 selected'}
            </Button>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {cuentasVisibles.length} {isSpanish ? (cuentasVisibles.length === 1 ? 'cuenta' : 'cuentas') : (cuentasVisibles.length === 1 ? 'account' : 'accounts')}
        </Typography>
      </Box>

      {/* 21 sep 2026 (Augusto: "las Tarjetas de crédito no deberían estar
          como cuentas sino como Deuda"). Se dice dónde están, en vez de
          que desaparezcan sin explicación. */}
      {cuentasDeDeudaOcultas > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/debts')}>
              {isSpanish ? 'Ir a Deudas' : 'Go to Debts'}
            </Button>
          }
        >
          {isSpanish
            ? `Tus tarjetas de crédito y préstamos (${cuentasDeDeudaOcultas}) se administran en Deudas. Acá van solo tus cuentas de ahorros y corrientes — la plata que tienes, no la que debes. Sus movimientos siguen intactos.`
            : `Your credit cards and loans (${cuentasDeDeudaOcultas}) are managed under Debts. This page lists only your savings and checking accounts — money you have, not money you owe. Their transactions are untouched.`}
        </Alert>
      )}

      {/* 25 sep 2026 (Augusto: "no se han juntado las cuentas iguales").
          PCM señala los candidatos; unir lo decide él. Ver
          gruposSospechosos arriba para por qué no se unen solas. */}
      {gruposSospechosos.map((grupo) => (
        <Alert
          key={grupo.map((c) => c.id).join('-')}
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => revisarGrupo(grupo)}>
              {isSpanish ? 'Revisar' : 'Review'}
            </Button>
          }
        >
          {isSpanish ? (
            <>
              <strong>{grupo.length} filas se llaman igual</strong> — {grupo[0].nombre} ({grupo[0].moneda}).
              {' '}Pueden ser la misma cuenta importada varias veces. Escribe el código de cuenta de cada
              una (doble clic debajo del nombre) para salir de dudas, o únelas si ya sabes que son la misma.
            </>
          ) : (
            <>
              <strong>{grupo.length} rows share the same name</strong> — {grupo[0].nombre} ({grupo[0].moneda}).
              {' '}They may be one account imported several times. Type each account number (double-click
              under the name) to be sure, or merge them if you already know they are the same.
            </>
          )}
        </Alert>
      ))}

      {/* Tabla de Cuentas */}
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small" aria-label={isSpanish ? 'tabla de cuentas' : 'accounts table'}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={algunaSeleccionada && !todasSeleccionadas}
                  checked={todasSeleccionadas}
                  onChange={toggleSeleccionarTodas}
                />
              </TableCell>
              <TableCell>{isSpanish ? 'Nombre' : 'Name'}</TableCell>
              <TableCell>{isSpanish ? 'Tipo' : 'Type'}</TableCell>
              <TableCell>{isSpanish ? 'Entidad' : 'Institution'}</TableCell>
              <TableCell align="right">{isSpanish ? 'Balance' : 'Balance'} {getMonedaSymbol(monedaLocal)} {monedaLocal}</TableCell>
              <TableCell align="right">{isSpanish ? 'Balance' : 'Balance'} {getMonedaSymbol(monedaSecundaria)} {monedaSecundaria}</TableCell>
              <TableCell align="center">{isSpanish ? 'Acciones' : 'Actions'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cuentasVisibles.map((cuenta) => {
              const editando = (field) => editingCell?.id === cuenta.id && editingCell?.field === field;
              return (
                <TableRow key={cuenta.id} hover selected={seleccionadas.has(cuenta.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={seleccionadas.has(cuenta.id)}
                      onChange={() => toggleSeleccion(cuenta.id)}
                    />
                  </TableCell>
                  <TableCell component="th" scope="row" onDoubleClick={() => startEdit(cuenta, 'nombre')}>
                    {editando('nombre') ? (
                      <TextField
                        autoFocus
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={commitEdit}
                        onKeyDown={handleEditKeyDown}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccountIconBadge nombre={cuenta.nombre} entidad={cuenta.entidad} tipo={cuenta.tipo} size={28} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {cuenta.nombre}
                          </Typography>
                          {/* 25 sep 2026 (Augusto: tres filas idénticas
                              "Cuenta Digital BCP SOLES", y después "no hay
                              cuenta 6923"). El código de cuenta es lo ÚNICO
                              que las distingue, y tiene que ser el código
                              ENTERO: un recorte de 4 dígitos no se parece a
                              nada de lo que el usuario ve en su extracto.
                              Doble clic para escribirlo a mano. */}
                          {editando('numero_cuenta') ? (
                            <TextField
                              autoFocus
                              size="small"
                              variant="standard"
                              placeholder="194-93394252-0-26"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={commitEdit}
                              onKeyDown={handleEditKeyDown}
                            />
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ cursor: 'text' }}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                startEdit(cuenta, 'numero_cuenta');
                              }}
                            >
                              {cuenta.numero_cuenta
                                || (cuenta.ultimos_digitos ? `••••${cuenta.ultimos_digitos}` : '')
                                || (isSpanish ? 'sin número — doble clic para escribirlo' : 'no account number — double-click to add')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell onDoubleClick={() => startEdit(cuenta, 'tipo')}>
                    {editando('tipo') ? (
                      <Select
                        autoFocus
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={commitEdit}
                        onClose={commitEdit}
                      >
                        {/* Solo activos: una tarjeta o un préstamo se
                            crean y se administran en Deudas. */}
                        {TIPOS_ACTIVO.map((t) => (
                          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Chip
                        label={getTipoLabel(cuenta)}
                        size="small"
                        color={
                          esTipoDeuda(cuenta.tipo)
                            ? 'error'
                            : cuenta.tipo === 'ahorros'
                            ? 'primary'
                            : 'default'
                        }
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell onDoubleClick={() => startEdit(cuenta, 'entidad')}>
                    {editando('entidad') ? (
                      <TextField
                        autoFocus
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={commitEdit}
                        onKeyDown={handleEditKeyDown}
                      />
                    ) : (
                      cuenta.entidad
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    onDoubleClick={() => puedeEditarBalanceAqui(cuenta, monedaLocal) && startEdit(cuenta, 'balance')}
                  >
                    {renderCeldaBalanceEditable(cuenta, monedaLocal)}
                  </TableCell>
                  <TableCell
                    align="right"
                    onDoubleClick={() => puedeEditarBalanceAqui(cuenta, monedaSecundaria) && startEdit(cuenta, 'balance')}
                  >
                    {renderCeldaBalanceEditable(cuenta, monedaSecundaria)}
                  </TableCell>
                  <TableCell align="center">
                    <ButtonGroup size="small" variant="text">
                      {/* 12 ago 2026: antes esto se hacía con un clic en
                          cualquier parte de la fila -- se movió a un botón
                          explícito porque ahora casi toda la fila es
                          editable con doble clic, y los dos gestos
                          chocaban (el primer clic de un doble clic
                          navegaba antes de poder editar). */}
                      <Tooltip title={isSpanish ? 'Ver transacciones de esta cuenta' : 'View transactions for this account'}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/transactions?account_id=${cuenta.id}`)}
                        >
                          <ReceiptLongIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isSpanish ? 'Editar cuenta' : 'Edit account'}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEdit(cuenta)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isSpanish ? 'Eliminar cuenta' : 'Delete account'}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteConfirmOpen(cuenta.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ButtonGroup>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 10 ago 2026 (Augusto: "no aparece un indicador si va a ingresar
          más cuentas o deudas o no"): confirmación explícita de que este
          paso ya quedó completo, con la opción de seguir agregando (botón
          "Agregar Nueva Cuenta" de arriba) o continuar a Deudas -- solo
          mientras el onboarding general no esté completo. */}
      {!loading && cuentas.length > 0 && onboardingData && !onboardingData.completed && (
        <Alert
          severity="success"
          sx={{ mb: 4 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/debts')}>
              {isSpanish ? 'Continuar' : 'Continue'}
            </Button>
          }
        >
          {isSpanish
            ? 'Este paso quedó listo. ¿Tienes otra cuenta o tarjeta? Agrégala con el botón "Agregar Nueva Cuenta" de arriba -- si ya terminaste, continúa a Deudas.'
            : 'This step is done. Have another account or card? Add it with the "Add New Account" button above -- once you\'re finished, continue to Debts.'}
        </Alert>
      )}

      {/* Dialog para agregar nueva cuenta */}
      <Dialog open={openNew} onClose={handleCloseNew} fullWidth maxWidth="sm">
        <DialogTitle>{isSpanish ? 'Agregar Nueva Cuenta' : 'Add New Account'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="nombre"
            label={isSpanish ? 'Nombre de la Cuenta' : 'Account Name'}
            type="text"
            fullWidth
            value={nuevaCuenta.nombre}
            onChange={handleChange}
            sx={{ mb: 2, mt: 1 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="tipo-cuenta-label">{isSpanish ? 'Tipo de Cuenta' : 'Account Type'}</InputLabel>
            <Select
              labelId="tipo-cuenta-label"
              name="tipo"
              value={nuevaCuenta.tipo}
              label={isSpanish ? 'Tipo de Cuenta' : 'Account Type'}
              onChange={handleChange}
            >
              {TIPOS_ACTIVO.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1, mb: 2 }}>
            {isSpanish
              ? '¿Es una tarjeta de crédito o un préstamo? Eso se registra en Deudas.'
              : 'A credit card or a loan? Those are registered under Debts.'}
          </Typography>

          {nuevaCuenta.tipo === 'otro' && (
            <TextField
              margin="dense"
              name="tipo_personalizado"
              label={isSpanish ? 'Especifica el tipo de deuda' : 'Specify the debt type'}
              type="text"
              fullWidth
              value={nuevaCuenta.tipo_personalizado}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
          )}

          {/* 21 sep 2026 (Augusto: "aparece como una cuenta... y no en la
              zona de Deudas"): una cuenta de tipo deuda ahora crea también
              su entrada en Deudas. Estos tres campos son lo que esa
              sección necesita para servir de algo -- calcular la cuota, el
              costo del financiamiento y avisar del vencimiento. Todos
              opcionales: exigir la tasa para registrar un préstamo
              familiar sería inventar un requisito que la vida real no
              tiene. */}
          {esTipoDeuda(nuevaCuenta.tipo) && nuevaCuenta.tipo !== 'tarjeta_credito' && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {isSpanish ? 'Datos del financiamiento (opcionales)' : 'Financing details (optional)'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {isSpanish
                  ? 'Esta deuda va a aparecer también en la sección Deudas. Con estos datos PCM puede calcular la cuota, el costo del financiamiento y avisarte antes de cada vencimiento.'
                  : 'This debt will also show up under Debts. With these details PCM can work out the installment, the financing cost, and warn you before each due date.'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  name="tasa_interes"
                  type="number"
                  label={isSpanish ? 'Tasa anual (%)' : 'Annual rate (%)'}
                  value={nuevaCuenta.tasa_interes || ''}
                  onChange={handleChange}
                  sx={{ flex: 1, minWidth: 120 }}
                />
                <TextField
                  size="small"
                  name="pago_mensual"
                  type="number"
                  label={isSpanish ? 'Cuota mensual' : 'Monthly payment'}
                  value={nuevaCuenta.pago_mensual || ''}
                  onChange={handleChange}
                  sx={{ flex: 1, minWidth: 120 }}
                />
                <TextField
                  size="small"
                  name="dia_vencimiento"
                  type="number"
                  label={isSpanish ? 'Día de vencimiento' : 'Due day'}
                  value={nuevaCuenta.dia_vencimiento || ''}
                  onChange={handleChange}
                  inputProps={{ min: 1, max: 31 }}
                  sx={{ flex: 1, minWidth: 120 }}
                />
              </Box>
            </Box>
          )}

          <Autocomplete
            freeSolo
            options={bancosSugeridos}
            value={nuevaCuenta.entidad}
            onChange={(_e, newValue) => setNuevaCuenta({ ...nuevaCuenta, entidad: newValue || '' })}
            onInputChange={(_e, newInputValue) => setNuevaCuenta({ ...nuevaCuenta, entidad: newInputValue })}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label={isSpanish ? 'Entidad Bancaria' : 'Bank / Institution'}
                fullWidth
                sx={{ mb: 2 }}
              />
            )}
          />

          {/* 4 ago 2026 (pedido de Augusto): vista previa del ícono que se
              va a mostrar para esta cuenta -- se detecta solo, a partir de
              lo que ya escribió en Nombre/Entidad (ver
              utils/accountIcons.js), sin que tenga que elegir nada aparte. */}
          {(nuevaCuenta.nombre || nuevaCuenta.entidad) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AccountIconBadge nombre={nuevaCuenta.nombre} entidad={nuevaCuenta.entidad} tipo={nuevaCuenta.tipo} size={30} />
              <Typography variant="caption" color="text.secondary">{isSpanish ? 'Así se verá el ícono de esta cuenta' : 'This is how this account\'s icon will look'}</Typography>
            </Box>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="moneda-label">{isSpanish ? 'Moneda' : 'Currency'}</InputLabel>
            <Select
              labelId="moneda-label"
              name="moneda"
              value={nuevaCuenta.moneda}
              label={isSpanish ? 'Moneda' : 'Currency'}
              onChange={handleChange}
            >
              <MenuItem value="USD">{nombreMoneda('USD')}</MenuItem>
              <MenuItem value="EUR">{nombreMoneda('EUR')}</MenuItem>
              <MenuItem value="GBP">{nombreMoneda('GBP')}</MenuItem>
              <MenuItem value="MXN">{nombreMoneda('MXN')}</MenuItem>
              <MenuItem value="COP">{nombreMoneda('COP')}</MenuItem>
              <MenuItem value="ARS">{nombreMoneda('ARS')}</MenuItem>
              <MenuItem value="CLP">{nombreMoneda('CLP')}</MenuItem>
              <MenuItem value="PEN">{nombreMoneda('PEN')}</MenuItem>
              <MenuItem value="BRL">{nombreMoneda('BRL')}</MenuItem>
              <MenuItem value="JPY">{nombreMoneda('JPY')}</MenuItem>
              <MenuItem value="CNY">{nombreMoneda('CNY')}</MenuItem>
              <MenuItem value="CAD">{nombreMoneda('CAD')}</MenuItem>
              <MenuItem value="AUD">{nombreMoneda('AUD')}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            margin="dense"
            name="balance"
            label={esTipoDeuda(nuevaCuenta.tipo) ? (isSpanish ? '¿Cuánto debes? (monto de la deuda)' : 'How much do you owe? (debt amount)') : (isSpanish ? 'Balance Inicial' : 'Initial Balance')}
            type="number"
            fullWidth
            value={nuevaCuenta.balance}
            onChange={handleChange}
            helperText={esTipoDeuda(nuevaCuenta.tipo) ? (isSpanish ? 'Ingresa el monto en positivo — se descuenta automáticamente del Balance Total.' : 'Enter the amount as positive — it\'s automatically subtracted from the Total Balance.') : ''}
          />
          {nuevaCuenta.tipo === 'tarjeta_credito' && (
            <TextField
              margin="dense"
              name="dia_cierre"
              label={isSpanish ? 'Día de cierre del ciclo (opcional)' : 'Billing cycle closing day (optional)'}
              type="number"
              fullWidth
              inputProps={{ min: 1, max: 31 }}
              value={nuevaCuenta.dia_cierre}
              onChange={handleChange}
              helperText={isSpanish ? 'Día del mes (1-31) en que cierra el ciclo de facturación, según tu estado de cuenta. Se usa para agrupar transacciones por \'Fecha de corte\'.' : 'Day of the month (1-31) your billing cycle closes, per your statement. Used to group transactions by \'Statement date\'.'}
            />
          )}
          {!esTipoDeuda(nuevaCuenta.tipo) && (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={!!nuevaCuenta.es_reserva}
                  onChange={(e) => setNuevaCuenta({ ...nuevaCuenta, es_reserva: e.target.checked })}
                />
              }
              label={isSpanish ? 'Cuenta de reserva / ahorro intocable' : 'Reserve account / untouchable savings'}
            />
          )}
          {nuevaCuenta.es_reserva && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1, mb: 1 }}>
              {isSpanish
                ? 'No aparecerá en el Calendario ni en Transacciones salvo que la selecciones explícitamente o actives "incluir reservas".'
                : 'Won\'t show up in Calendar or Transactions unless you select it explicitly or turn on "include reserves".'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNew}>{isSpanish ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={() => handleCreateAccount(nuevaCuenta)} variant="contained" color="primary">
            {isSpanish ? 'Guardar' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para editar cuenta */}
      <Dialog open={openEdit} onClose={handleCloseEdit} fullWidth maxWidth="sm">
        <DialogTitle>{isSpanish ? 'Editar Cuenta' : 'Edit Account'}</DialogTitle>
        <DialogContent>
          {cuentaEditando && (
            <>
              <TextField
                autoFocus
                margin="dense"
                name="nombre"
                label={isSpanish ? 'Nombre de la Cuenta' : 'Account Name'}
                type="text"
                fullWidth
                value={cuentaEditando.nombre}
                onChange={handleEditChange}
                sx={{ mb: 2, mt: 1 }}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="tipo-cuenta-edit-label">{isSpanish ? 'Tipo de Cuenta' : 'Account Type'}</InputLabel>
                <Select
                  labelId="tipo-cuenta-edit-label"
                  name="tipo"
                  value={cuentaEditando.tipo}
                  label={isSpanish ? 'Tipo de Cuenta' : 'Account Type'}
                  onChange={handleEditChange}
                >
                  {TIPOS_ACTIVO.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                  {/* Si esta cuenta ya era una deuda (dato viejo), su tipo
                      se sigue ofreciendo para no borrárselo sin querer al
                      abrir el formulario. */}
                  {esTipoDeuda(cuentaEditando.tipo) && (
                    <MenuItem value={cuentaEditando.tipo}>{getTipoLabel(cuentaEditando)}</MenuItem>
                  )}
                </Select>
              </FormControl>

              {cuentaEditando.tipo === 'otro' && (
                <TextField
                  margin="dense"
                  name="tipo_personalizado"
                  label={isSpanish ? 'Especifica el tipo de deuda' : 'Specify the debt type'}
                  type="text"
                  fullWidth
                  value={cuentaEditando.tipo_personalizado || ''}
                  onChange={handleEditChange}
                  sx={{ mb: 2 }}
                />
              )}

              <Autocomplete
                freeSolo
                options={bancosSugeridos}
                value={cuentaEditando.entidad}
                onChange={(_e, newValue) => setCuentaEditando({ ...cuentaEditando, entidad: newValue || '' })}
                onInputChange={(_e, newInputValue) => setCuentaEditando({ ...cuentaEditando, entidad: newInputValue })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="dense"
                    label={isSpanish ? 'Entidad Bancaria' : 'Bank / Institution'}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />

              {(cuentaEditando.nombre || cuentaEditando.entidad) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AccountIconBadge nombre={cuentaEditando.nombre} entidad={cuentaEditando.entidad} tipo={cuentaEditando.tipo} size={30} />
                  <Typography variant="caption" color="text.secondary">{isSpanish ? 'Así se verá el ícono de esta cuenta' : 'This is how this account\'s icon will look'}</Typography>
                </Box>
              )}

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="moneda-edit-label">{isSpanish ? 'Moneda' : 'Currency'}</InputLabel>
                <Select
                  labelId="moneda-edit-label"
                  name="moneda"
                  value={cuentaEditando.moneda}
                  label={isSpanish ? 'Moneda' : 'Currency'}
                  onChange={handleEditChange}
                >
                  <MenuItem value="USD">{nombreMoneda('USD')}</MenuItem>
                  <MenuItem value="EUR">{nombreMoneda('EUR')}</MenuItem>
                  <MenuItem value="GBP">{nombreMoneda('GBP')}</MenuItem>
                  <MenuItem value="MXN">{nombreMoneda('MXN')}</MenuItem>
                  <MenuItem value="COP">{nombreMoneda('COP')}</MenuItem>
                  <MenuItem value="ARS">{nombreMoneda('ARS')}</MenuItem>
                  <MenuItem value="CLP">{nombreMoneda('CLP')}</MenuItem>
                  <MenuItem value="PEN">{nombreMoneda('PEN')}</MenuItem>
                  <MenuItem value="BRL">{nombreMoneda('BRL')}</MenuItem>
                  <MenuItem value="JPY">{nombreMoneda('JPY')}</MenuItem>
                  <MenuItem value="CNY">{nombreMoneda('CNY')}</MenuItem>
                  <MenuItem value="CAD">{nombreMoneda('CAD')}</MenuItem>
                  <MenuItem value="AUD">{nombreMoneda('AUD')}</MenuItem>
                </Select>
              </FormControl>

              <TextField
                margin="dense"
                name="balance"
                label={esTipoDeuda(cuentaEditando.tipo) ? (isSpanish ? '¿Cuánto debes? (monto de la deuda)' : 'How much do you owe? (debt amount)') : (isSpanish ? 'Balance' : 'Balance')}
                type="number"
                fullWidth
                value={cuentaEditando.balance}
                onChange={handleEditChange}
                helperText={esTipoDeuda(cuentaEditando.tipo) ? (isSpanish ? 'Ingresa el monto en positivo — se descuenta automáticamente del Balance Total.' : 'Enter the amount as positive — it\'s automatically subtracted from the Total Balance.') : ''}
              />
              {cuentaEditando.tipo === 'tarjeta_credito' && (
                <TextField
                  margin="dense"
                  name="dia_cierre"
                  label={isSpanish ? 'Día de cierre del ciclo (opcional)' : 'Billing cycle closing day (optional)'}
                  type="number"
                  fullWidth
                  inputProps={{ min: 1, max: 31 }}
                  value={cuentaEditando.dia_cierre ?? ''}
                  onChange={handleEditChange}
                  helperText={isSpanish ? 'Día del mes (1-31) en que cierra el ciclo de facturación, según tu estado de cuenta. Se usa para agrupar transacciones por \'Fecha de corte\'.' : 'Day of the month (1-31) your billing cycle closes, per your statement. Used to group transactions by \'Statement date\'.'}
                />
              )}
              {!esTipoDeuda(cuentaEditando.tipo) && (
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Switch
                      checked={!!cuentaEditando.es_reserva}
                      onChange={(e) => setCuentaEditando({ ...cuentaEditando, es_reserva: e.target.checked })}
                    />
                  }
                  label={isSpanish ? 'Cuenta de reserva / ahorro intocable' : 'Reserve account / untouchable savings'}
                />
              )}
              {cuentaEditando.es_reserva && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1, mb: 1 }}>
                  {isSpanish
                    ? 'No aparecerá en el Calendario ni en Transacciones salvo que la selecciones explícitamente o actives "incluir reservas".'
                    : 'Won\'t show up in Calendar or Transactions unless you select it explicitly or turn on "include reserves".'}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>{isSpanish ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={handleActualizarCuenta} variant="contained" color="primary">
            {isSpanish ? 'Guardar Cambios' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación de eliminación */}
      <Dialog
        open={openDelete}
        onClose={handleDeleteConfirmClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {isSpanish ? '¿Eliminar cuenta y todo su historial?' : 'Delete account and all its history?'}
        </DialogTitle>
        <DialogContent>
          {/* 3 ago 2026: se aclara que las transacciones también se borran.
              25 sep 2026 (Augusto: "siempre colocar mensaje que al borrar
              se elimina todo y se pierde todo si está seguro"): el texto
              decía "transacciones asociadas", que suena a un vínculo que se
              corta. Se borran de verdad, y con ellas los saldos de
              extracto y las conciliaciones. Se dice qué se pierde, no que
              "no se puede deshacer". */}
          <Alert severity="error" sx={{ mb: 2 }}>
            {isSpanish
              ? 'Se borra TODO y no hay forma de recuperarlo: la cuenta, todos sus movimientos, los saldos de sus extractos importados y sus conciliaciones.'
              : 'EVERYTHING is deleted with no way to recover it: the account, all its transactions, the balances from its imported statements, and its reconciliations.'}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {isSpanish
              ? 'Si lo que quieres es juntar dos filas que son la misma cuenta, no borres ninguna: márcalas y usa "Unir las 2 seleccionadas". Ahí no se pierde nada.'
              : 'If what you want is to merge two rows that are the same account, do not delete either: select them and use "Merge the 2 selected". Nothing is lost there.'}
          </Typography>
          {errorBorrado && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorBorrado}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose}>{isSpanish ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={handleEliminarCuenta} color="error" autoFocus>
            {isSpanish ? 'Eliminar' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 12 ago 2026: confirmación de borrado masivo.
          28 ago 2026 (pedido de Augusto: "eso tiene que ser extremadamente
          cuidadoso"): un solo click en "Eliminar" ya no alcanza -- hay que
          escribir el número de cuentas o "ELIMINAR" para habilitar el
          botón, como salvaguarda extra contra un click accidental sobre
          datos financieros que no tienen forma de recuperarse. */}
      {/* 25 sep 2026 (Augusto: "quiero borrar a cero, transacciones y
          cuentas, menos los Presupuestos"). Se muestran los números ANTES
          de confirmar y se pide escribir la frase: un botón que borra
          todo sin decir cuánto obliga a confiar; con los números se puede
          verificar. */}
      <Dialog open={openReinicio} onClose={() => setOpenReinicio(false)} fullWidth maxWidth="sm">
        <DialogTitle>{isSpanish ? 'Empezar de cero' : 'Start over'}</DialogTitle>
        <DialogContent>
          {reinicioError && <Alert severity="error" sx={{ mb: 2 }}>{reinicioError}</Alert>}
          {!reinicioPreview ? (
            <Typography variant="body2" color="text.secondary">
              {isSpanish ? 'Revisando qué hay...' : 'Checking what you have...'}
            </Typography>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {isSpanish
                  ? 'Esto borra tus datos para que puedas volver a importar todo desde cero. No se puede deshacer.'
                  : 'This deletes your data so you can re-import everything from scratch. It cannot be undone.'}
              </Alert>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {isSpanish ? 'Se borra' : 'Will be deleted'}
              </Typography>
              <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
                {Object.entries(reinicioPreview.se_borra).map(([clave, valor]) => (
                  <li key={clave}>
                    <Typography variant="body2">
                      {valor} {clave.replace(/_/g, ' ')}
                    </Typography>
                  </li>
                ))}
              </Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {isSpanish ? 'Se conserva' : 'Will be kept'}
              </Typography>
              <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
                {Object.entries(reinicioPreview.se_conserva).map(([clave, valor]) => (
                  <li key={clave}>
                    <Typography variant="body2">
                      {valor} {clave.replace(/_/g, ' ')}
                    </Typography>
                  </li>
                ))}
              </Box>
              {/* 25 sep 2026: qué salvar. Conservar una cuenta conserva
                  también sus movimientos, saldos declarados y
                  conciliaciones -- salvarla vacía, con un saldo que ya no
                  se puede explicar, sería peor que borrarla. */}
              {(reinicioPreview.cuentas?.length > 0 || reinicioPreview.deudas?.length > 0) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {isSpanish ? '¿Quieres salvar alguna?' : 'Want to keep any of these?'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {isSpanish
                      ? 'Una cuenta que marques se queda con todos sus movimientos e historial. Lo que no marques, se borra.'
                      : 'An account you tick keeps all its transactions and history. Anything unticked is deleted.'}
                  </Typography>
                  {reinicioPreview.cuentas?.map((c) => (
                    <FormControlLabel
                      key={`c-${c.id}`}
                      sx={{ display: 'block', ml: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={conservarCuentas.has(c.id)}
                          onChange={() => alternarConservar(setConservarCuentas)(c.id)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {c.nombre}
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {c.moneda} · {c.movimientos} {isSpanish ? 'movimientos' : 'transactions'}
                          </Typography>
                        </Typography>
                      }
                    />
                  ))}
                  {reinicioPreview.deudas?.map((d) => (
                    <FormControlLabel
                      key={`d-${d.id}`}
                      sx={{ display: 'block', ml: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={conservarDeudas.has(d.id)}
                          onChange={() => alternarConservar(setConservarDeudas)(d.id)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {d.nombre}
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {isSpanish ? 'deuda' : 'debt'} · {d.moneda} {Number(d.monto || 0).toFixed(2)}
                          </Typography>
                        </Typography>
                      }
                    />
                  ))}
                  <Divider sx={{ my: 2 }} />
                </>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {isSpanish
                  ? 'Tus series recurrentes se quedan. Las que apunten a una cuenta borrada pierden esa cuenta: vuelve a elegirla en Recurrentes después de importar.'
                  : 'Your recurring series stay. Ones pointing at a deleted account lose it: pick it again under Recurring after importing.'}
              </Typography>
              <TextField
                fullWidth
                size="small"
                label={isSpanish
                  ? `Escribe "${reinicioPreview.confirmacion_requerida}" para confirmar`
                  : `Type "${reinicioPreview.confirmacion_requerida}" to confirm`}
                value={reinicioTexto}
                onChange={(e) => setReinicioTexto(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReinicio(false)}>{isSpanish ? 'Cancelar' : 'Cancel'}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={
              reiniciando
              || !reinicioPreview
              || reinicioTexto.trim().toUpperCase() !== reinicioPreview.confirmacion_requerida
            }
            onClick={confirmarReinicio}
          >
            {isSpanish ? 'Borrar y empezar de cero' : 'Delete and start over'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openBulkDelete} onClose={handleBulkDeleteClose}>
        <DialogTitle>{isSpanish ? `¿Eliminar ${seleccionadas.size} cuentas?` : `Delete ${seleccionadas.size} accounts?`}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {isSpanish
              ? 'Esta acción no se puede deshacer y también eliminará todas las transacciones asociadas a cada cuenta seleccionada.'
              : 'This action cannot be undone and will also delete all transactions linked to each selected account.'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {isSpanish ? (
              <>Para confirmar, escribe <strong>{seleccionadas.size}</strong> (el número de cuentas) o la palabra <strong>ELIMINAR</strong>.</>
            ) : (
              <>To confirm, type <strong>{seleccionadas.size}</strong> (the number of accounts) or the word <strong>DELETE</strong>.</>
            )}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={bulkDeleteConfirmText}
            onChange={(e) => setBulkDeleteConfirmText(e.target.value)}
            placeholder={isSpanish ? `${seleccionadas.size} o ELIMINAR` : `${seleccionadas.size} or DELETE`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDeleteClose} disabled={bulkDeleting}>{isSpanish ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={handleBulkDelete} color="error" disabled={bulkDeleting || !bulkDeleteConfirmed} autoFocus>
            {bulkDeleting ? (isSpanish ? 'Eliminando...' : 'Deleting...') : (isSpanish ? 'Eliminar' : 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 25 sep 2026: unir dos cuentas (ver handleUnir arriba). */}
      <Dialog open={openUnir} onClose={() => setOpenUnir(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isSpanish ? 'Unir dos cuentas en una' : 'Merge two accounts into one'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isSpanish
              ? 'No se pierde nada: los movimientos, los saldos de extracto y las conciliaciones de la cuenta que se va pasan a la que se queda. El saldo final será el del extracto más reciente de las dos.'
              : 'Nothing is lost: transactions, statement balances and reconciliations from the account that goes are moved into the one that stays. The final balance will be the one from the most recent statement of the two.'}
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {isSpanish ? '¿Cuál se queda?' : 'Which one stays?'}
          </Typography>
          <RadioGroup value={destinoUnion ?? ''} onChange={(e) => setDestinoUnion(Number(e.target.value))}>
            {cuentasSeleccionadas.map((c) => (
              <FormControlLabel
                key={c.id}
                value={c.id}
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2">
                      {c.nombre}
                      {c.ultimos_digitos ? ` ••••${c.ultimos_digitos}` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {c.moneda} {Number(c.balance ?? 0).toLocaleString('es-PE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
          {errorUnion && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorUnion}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUnir(false)} disabled={uniendo}>
            {isSpanish ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button onClick={handleUnir} variant="contained" disabled={uniendo || !destinoUnion}>
            {uniendo ? (isSpanish ? 'Uniendo...' : 'Merging...') : (isSpanish ? 'Unir' : 'Merge')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CuentasPage;
